import { buildCurriculum, durationLabel, MIN_SESSIONS, sumSessions } from './curriculum'
import type { Answers, CurriculumTopic, InhouseJudgement, JudgeResult, ModuleId, ProposalType } from './types'
import { TYPE_NAMES } from './types'

// §3.1 型の判定。上から順に評価し、最初に合致した型を採用する。この順序を変えてはいけない。
export function decideType(a: Answers): ProposalType {
  // R1 → 型D 組織育成型
  if (a.Q9 === 'q9_10up') return 'D'
  if (a.Q9 === 'q9_4_9' && a.Q10 === 'q10_gap') return 'D'
  if (a.Q12 === 'q12_system') return 'D'
  if (a.Q1 === 'q1_person' && a.Q9 !== 'q9_1') return 'D'

  // R2 → 型C 伴走・実践型
  const running = a.Q4.some((v) => v !== 'q4_none')
  if (running && ['q6_shared', 'q6_inhouse'].includes(a.Q6) && ['q7_cannot', 'q7_hypo'].includes(a.Q7)) {
    return 'C'
  }

  // R3 → 型B 体系習得型
  if (a.Q5.includes('q5_all')) return 'B'
  if (a.Q5.length >= 3) return 'B'
  if (a.Q10 === 'q10_none') return 'B'
  if (a.Q1 === 'q1_loss' && a.Q4.includes('q4_none')) return 'B'
  if (a.Q12 === 'q12_full' && ['q10_none', 'q10_self1'].includes(a.Q10)) return 'B'

  // R4 → 型A ピンポイント強化型（デフォルト）
  return 'A'
}

// §4 追加モジュール。複数同時発火しうる。
export function decideModules(a: Answers): ModuleId[] {
  const modules: ModuleId[] = []

  if (a.Q6 === 'q6_full_agency' || a.Q8 === 'q8_agency_only') modules.push('M1')
  if (a.Q3.includes('q3_training')) modules.push('M2')
  if (a.Q3.includes('q3_hire')) modules.push('M3')
  if (a.Q1 === 'q1_loss' || ['q5_sns', 'q5_seo', 'q5_ai'].some((v) => a.Q5.includes(v))) modules.push('M4')
  if (['q14_want', 'q14_unknown'].includes(a.Q14)) modules.push('M5')
  if (a.Q13 === 'q13_asap') modules.push('M6')

  return modules
}

const INHOUSE_TEXT: Record<InhouseJudgement['level'], { text: string; reason: string }> = {
  only: {
    text: '研修のみで足ります',
    reason: '伴走は不要です。講座で判断軸を持てば、自走できる状態にあります',
  },
  support: {
    text: '研修＋伴走を推奨します',
    reason: '知識だけでは埋まりません。自社アカウントを見ながらの伴走が必要です',
  },
  system: {
    text: '研修＋仕組み化を推奨します',
    reason: '個人のスキルではなく、育成の型を組織に残す設計が必要です',
  },
}

// §7 内製化の要否。3段階のいずれかを必ず返す。
export function decideInhouse(a: Answers, type: ProposalType): InhouseJudgement {
  let level: InhouseJudgement['level']
  if (type === 'D') {
    level = 'system'
  } else if (type === 'C') {
    level = 'support'
  } else if (a.Q12 === 'q12_full' && ['q7_receive', 'q7_cannot'].includes(a.Q7)) {
    level = 'support'
  } else if (['q12_talk', 'q12_partial'].includes(a.Q12) && ['q7_hypo', 'q7_analyze'].includes(a.Q7)) {
    level = 'only'
  } else {
    level = 'support'
  }

  return { level, ...INHOUSE_TEXT[level] }
}

export function judge(a: Answers, topics: CurriculumTopic[] = []): JudgeResult {
  const type = decideType(a)
  const modules = decideModules(a)

  let curriculum = buildCurriculum(a, type, topics)
  if (modules.includes('M1')) {
    curriculum = [
      {
        order: 0,
        name: '広告アカウントの管理権限を自社で取得する',
        aim: '受講前の準備。中身が見えない状態では講義の効果が限定的になる',
        count: null,
        prep: true,
      },
      ...curriculum,
    ]
  }

  const totalSessions = sumSessions(curriculum)
  // §6.2: 最小5回ルール。下回る場合は補完ロジックの実装バグとして扱う（フォールバックしない）。
  if (totalSessions < MIN_SESSIONS) {
    throw new Error(`最小${MIN_SESSIONS}回ルール違反: total_sessions=${totalSessions}`)
  }

  return {
    type,
    typeName: TYPE_NAMES[type],
    modules,
    curriculum,
    totalSessions,
    duration: durationLabel(totalSessions),
    inhouse: decideInhouse(a, type),
  }
}
